import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useState } from 'react';
import { getAllRoutes } from '@lognai/frontend-core';
import { ThemeProvider, Toaster } from '@lognai/synapse-design-system-v2';
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PartnerInviteAcceptPage from './pages/auth/PartnerInviteAcceptPage';
import { InvitationAcceptPage } from '@lognai/plugin-admin-ui';
import PartnerInviteExpiredPage from './pages/auth/PartnerInviteExpiredPage';
import SupplierRegistrationPage from './pages/auth/SupplierRegistrationPage';
import WorkspacePage from './pages/WorkspacePage';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import { RoutePermissionGuard } from './components/RoutePermissionGuard';
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import { loadPluginsForUser } from './plugins/loader';
import { useAuth } from './hooks/useAuth';
import { setCompanyPrimaryColor } from './utils/company-primary-color';
import { InsightsPage } from './pages/InsightsPage';
import SettingsPage from './pages/SettingsPage';
import { isRetryableApiError } from '@lognai/plugin-sdk';
const MembersPage = lazy(() => import('./pages/members/MembersPage'));

const DraftsPage = lazy(() =>
  import('@lognai/plugin-ai-core-ui').then((m) => ({ default: m.DraftsPage }))
);

// Public (no-login) ad-hoc supplier quote page (token link).
const PublicProcurementQuotePage = lazy(() =>
  import('@lognai/plugin-procurement-ui').then((m) => ({ default: m.PublicQuotePage }))
);

// Public (no-login) page where a technical approver rules on an alternate part (token link).
const PublicTechnicalApprovalPage = lazy(() =>
  import('@lognai/plugin-procurement-ui').then((m) => ({ default: m.TechnicalApprovalPage }))
);

// Public (no-login) ad-hoc carrier quote page (token link).
const PublicFreightRfqQuotePage = lazy(() =>
  import('@lognai/plugin-freight-rfq-ui').then((m) => ({ default: m.PublicQuotePage }))
);

// Public (no-login) shared shipment tracking page (token link).
const PublicShipmentTrackingPage = lazy(() =>
  import('@lognai/plugin-shipments-ui').then((m) => ({ default: m.PublicTrackingPage }))
);

// Command Center — full-viewport canvas page (no app chrome).
const CommandCenterPage = lazy(() => import('./pages/command-center/CommandCenterPage'));

// Loading component
const LoadingScreen = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div>Loading...</div>
  </div>
);

/**
 * Shown when the active company could not be resolved after retrying.
 *
 * The alternative — and what the app used to do — is to carry on with an empty
 * entitlement set, which silently prunes the sidebar to whatever it can still
 * resolve and renders the generic "Page Not Found" on every plugin route. That
 * is worse than an error: it tells the user their modules do not exist, and it
 * does it to an admin holding `["*"]` (QA ledger AUTH2-001).
 *
 * Saying "we could not load this" and offering the retry is the honest version
 * of the same failure.
 */
const WorkspaceUnavailable = ({ onRetry }: { onRetry: () => void }) => (
  <div
    role="alert"
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      gap: '0.75rem',
      padding: '1.5rem',
      textAlign: 'center',
    }}
  >
    <h1 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>
      Couldn&rsquo;t load your workspace
    </h1>
    <p style={{ margin: 0, maxWidth: '32rem', opacity: 0.75 }}>
      We couldn&rsquo;t reach the server to find out which modules you have access to. Your sign-in
      is fine &mdash; this is usually temporary.
    </p>
    <button
      type="button"
      onClick={onRetry}
      style={{
        marginTop: '0.25rem',
        padding: '0.5rem 1rem',
        borderRadius: '0.375rem',
        border: '1px solid currentColor',
        background: 'transparent',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      Try again
    </button>
  </div>
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      /**
       * Retry what can succeed, and only that.
       *
       * A flat `retry: 1` retried a 403 (which will refuse identically
       * forever) and gave a rate-limited or flaky request exactly one
       * immediate second chance — which for a 429 is another 429. That is how
       * a screen ends up intermittently blank with a permissions-sounding
       * error on a tenant that has full access (QA ledger DND-001).
       *
       * isRetryableApiError reads the HTTP status the plugin fetch wrappers
       * now attach: 4xx refusals stop immediately, 408/425/429 and 5xx get up
       * to two more tries, and an error with no status at all — a transport
       * failure — is treated as the most retryable kind.
       */
      retry: (failureCount, error) => failureCount < 2 && isRetryableApiError(error),
      // Backing off matters more than the count here: the limiter's window is
      // measured in seconds, so 1s then 4s clears it where two instant retries
      // just spend the quota faster.
      retryDelay: (attempt) => Math.min(1000 * 4 ** attempt, 10_000),
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * App Routes Component
 *
 * Handles dynamic plugin loading and route rendering
 */
function AppRoutes() {
  const {
    user,
    company,
    isLoading: authLoading,
    companyResolutionFailed,
    retryCompanyResolution,
  } = useAuth();
  const [pluginsLoaded, setPluginsLoaded] = useState(false);
  const [pluginRoutes, setPluginRoutes] = useState<ReturnType<typeof getAllRoutes>>([]);

  // The active company's brand colour, if it has one. No company (signed out, or a
  // platform admin with none selected) means the default — the login page is never
  // tinted by whoever signed in last.
  const brandLight = company?.branding?.primaryColor;
  const brandDark = company?.branding?.primaryColorDark;
  useEffect(() => {
    setCompanyPrimaryColor(brandLight ? { light: brandLight, dark: brandDark } : null);
  }, [brandLight, brandDark]);

  useEffect(() => {
    // Wait for auth to complete before loading plugins
    if (authLoading) return;

    const initPlugins = async () => {
      // Get user roles and company plugins
      const userRoles = user?.roles || [];
      // Prefer backend-computed effective plugins (per active-company context, partner-aware).
      // Fall back to enabledPlugins only if an older API response lacks the field.
      const baseCompanyPlugins = company?.effectivePlugins?.length
        ? company.effectivePlugins
        : (company?.enabledPlugins ?? []);

      // Analytics is a normal per-tenant plugin included by default for non-partner
      // companies. This union is a safety net for API responses that predate the
      // effectivePlugins field; the route/nav is hidden for anyone lacking the permission.
      const isPartner = company?.type === 'PARTNER';
      const companyPlugins = isPartner
        ? baseCompanyPlugins
        : Array.from(new Set([...baseCompanyPlugins, 'analytics']));

      // Strict form, matching core-platform's `isPlatformAdmin`: the role AND no
      // company. `companyId == null` alone is not enough — an invited member
      // whose company link has not been stamped yet has exactly that shape.
      const isPlatformAdmin =
        (user?.roles?.includes('platform_admin') ?? false) && user?.companyId == null;

      await loadPluginsForUser(userRoles, companyPlugins, {
        isExternalPartner: isPartner,
        isPlatformAdmin,
      });

      // Get registered routes from plugins
      const routes = getAllRoutes();
      setPluginRoutes(routes);
      setPluginsLoaded(true);
    };

    initPlugins();
    // `user?.companyId` is a dependency because it is half of the platform-admin
    // test above — roles alone would not re-run this when the company context
    // changes.
  }, [
    authLoading,
    user?.roles?.join(','),
    user?.companyId,
    company?.enabledPlugins?.join(','),
    company?.type,
  ]);

  /*
    A signed-in user whose token names a company MUST have a resolvable company.
    If we have the one and not the other, the answer has not arrived — it is not
    "this company has no modules". Loading the plugin set from that state is
    what pruned the sidebar to four sections and rendered the generic 404 on
    every plugin route, for an admin holding `["*"]`, whenever an entitlement
    call was rate-limited (QA ledger AUTH2-001).

    Platform admins have no company by design and are excluded by the
    `user.companyId` test, so they still load normally.
  */
  const companyUnresolved = !!user?.companyId && !company;

  if (authLoading || !pluginsLoaded || (companyUnresolved && !companyResolutionFailed)) {
    return <LoadingScreen />;
  }

  if (companyUnresolved) {
    // Bounded: we asked, we retried, it is still not there. Say so and offer a
    // way forward, rather than showing a shell that quietly lies about what
    // this account can reach.
    return <WorkspaceUnavailable onRetry={retryCompanyResolution} />;
  }

  // Render a plugin route's element (handles lazy vs direct components).
  // Wraps in RoutePermissionGuard so URL-typed access is gated even though the
  // sidebar already hides routes the active account can't access.
  const renderPluginElement = (route: ReturnType<typeof getAllRoutes>[number]) => {
    const RouteComponent = typeof route.component === 'function'
      ? lazy(route.component as () => Promise<{ default: React.ComponentType }>)
      : route.component;
    return (
      <RoutePermissionGuard permissions={route.permissions}>
        <Suspense fallback={<LoadingScreen />}>
          <RouteComponent />
        </Suspense>
      </RoutePermissionGuard>
    );
  };

  // Plugin routes registered with `requiresAuth: false` (e.g. admin-ui's
  // /invite/accept) are reachable WITHOUT a session: an invitee follows an
  // email link before they have an account. These render outside
  // <ProtectedRoute> and without the permission guard (there is no user to
  // check). Everything else stays behind auth. This flag was previously
  // declared by plugins but never read here, so "public" plugin routes
  // bounced anonymous visitors to /login.
  const publicPluginRoutes = pluginRoutes.filter((r) => r.requiresAuth === false);
  const authedPluginRoutes = pluginRoutes.filter((r) => r.requiresAuth !== false);

  // Workflow *execution* screens (e.g. pick/pack/ship) are floor-operator,
  // often tablet/handheld, surfaces. They render full-screen — behind auth
  // but WITHOUT the app sidebar/header chrome — for a focused, kiosk-style
  // experience driven by the ExecutionShell component.
  const isFullScreenRoute = (path: string) => path.includes('/workflows/') && path.includes('/execute');
  const fullScreenRoutes = authedPluginRoutes.filter((r) => isFullScreenRoute(r.path));
  const chromeRoutes = authedPluginRoutes.filter((r) => !isFullScreenRoute(r.path));

  // Public plugin routes render bare (Suspense only) — no auth, no
  // permission guard, no app chrome.
  const renderPublicPluginElement = (route: ReturnType<typeof getAllRoutes>[number]) => {
    const RouteComponent = typeof route.component === 'function'
      ? lazy(route.component as () => Promise<{ default: React.ComponentType }>)
      : route.component;
    return (
      <Suspense fallback={<LoadingScreen />}>
        <RouteComponent />
      </Suspense>
    );
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/partner-invite/accept" element={<PartnerInviteAcceptPage />} />
        <Route path="/partner-invite/expired" element={<PartnerInviteExpiredPage />} />
        <Route path="/supplier-registration" element={<SupplierRegistrationPage />} />
      </Route>

      {/* Invitation accept (token link, no login required).
          /accept-invitation is the path used in invitation emails;
          /invite is the admin-ui plugin's route for the same page. */}
      <Route path="/invite" element={<InvitationAcceptPage />} />
      <Route path="/accept-invitation" element={<InvitationAcceptPage />} />

      {/* Public ad-hoc supplier quote (token link, no login, no auth layout) */}
      <Route
        path="/procurement/quote/:token"
        element={
          <Suspense fallback={<LoadingScreen />}>
            <PublicProcurementQuotePage />
          </Suspense>
        }
      />

      {/* Public technical approval of an alternate part (token link, no login, no auth layout) */}
      <Route
        path="/procurement/technical-approval/:token"
        element={
          <Suspense fallback={<LoadingScreen />}>
            <PublicTechnicalApprovalPage />
          </Suspense>
        }
      />

      {/* Public ad-hoc carrier quote (token link, no login, no auth layout) */}
      <Route
        path="/freight-rfq/quote/:token"
        element={
          <Suspense fallback={<LoadingScreen />}>
            <PublicFreightRfqQuotePage />
          </Suspense>
        }
      />

      {/* Public shared shipment tracking (token link, no login, no auth layout).
          Explicit like the quote pages above: the plugin loader only runs for a
          signed-in session, so a route that depends on it bounces visitors to /login. */}
      <Route
        path="/shipments/track/:token"
        element={
          <Suspense fallback={<LoadingScreen />}>
            <PublicShipmentTrackingPage />
          </Suspense>
        }
      />

      {/* Plugin routes declared requiresAuth: false (token links, no login) */}
      {publicPluginRoutes.map((route) => (
        <Route key={route.id} path={route.path} element={renderPublicPluginElement(route)} />
      ))}

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* Full-screen plugin routes (no app chrome) — matched before the
            chrome layout so their explicit paths win over its catch-all. */}
        {fullScreenRoutes.map((route) => (
          <Route key={route.id} path={route.path} element={renderPluginElement(route)} />
        ))}

        {/* Command Center — full-viewport, no MainLayout chrome. */}
        <Route
          path="/command-center"
          element={
            <RoutePermissionGuard permissions={['shipment:shipment:read']}>
              <Suspense fallback={<LoadingScreen />}>
                <CommandCenterPage />
              </Suspense>
            </RoutePermissionGuard>
          }
        />

        <Route element={<MainLayout />}>
          <Route
            path="/"
            element={
              <Navigate
                to={company?.type === 'PARTNER' ? '/vendor-onboarding/my-application' : '/workspace'}
                replace
              />
            }
          />
          {/* The dashboard was retired in favour of the workspace; the path is
              kept as a redirect so bookmarks and "Home" breadcrumbs still land. */}
          <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route
            path="/drafts"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <DraftsPage />
              </Suspense>
            }
          />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/companies/:companyId/members"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <MembersPage />
              </Suspense>
            }
          />

          {/* Dynamic plugin routes (with app chrome) */}
          {chromeRoutes.map((route) => (
            <Route key={route.id} path={route.path} element={renderPluginElement(route)} />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        {/* Renders whatever `useToast()` queues — without it every toast in the
            app (including API error messages) is silently dropped. */}
        <Toaster />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
